import React, { useEffect, useState, useRef, useCallback, useMemo, memo } from "react";
import Button from "../design/Shared/Button";
import icon from "../../images/logo.svg";
import popular from "../../images/icons/Popular.svg";
import personal from "../../images/icons/Personal.svg";

import AutoGrowTextArea from "./AutoGrowTextArea";
import NounAwareTextArea from "./NounAwareTextArea";
import { useMilaContext } from "../context/useMilaContext";
import apiClient from "../../business/network/ApiClient";
import { CurrentUser } from "../../business/helper/CurrentUser";
import { GrtAnalyticsGA4 } from "./GrtAnalyticsGA4";
import { useNounRecognition } from "../../hooks/useNounRecognition";
import { useMentionCompletion } from "../../hooks/useMentionCompletion";

const PopularIcon = memo(() => (
  <img src={popular} alt="Popular" width={12} height={12} />
));

const PersonalIcon = memo(() => (
  <img src={personal} alt="Personal" width={12} height={12} />
));

const DEBOUNCE_MS = 400;
const MIN_CHARS = 5;

// only re-renders when its own props change, not on every keystroke
const SuggestionItem = memo(({ text, isActive, onSelect, onHover, icon }) => (
  <button
    type="button"
    onMouseDown={(e) => {
      e.preventDefault();
      onSelect(text);
    }}
    onMouseEnter={onHover}
    className={`w-full flex items-center gap-3 px-5 py-2 text-left text-sm
                transition-colors duration-100 group text-[15px]
                ${isActive
        ? "bg-[#F5F3FF] text-[#394150]"
        : "text-[#394150] hover:bg-[#F5F3FF] hover:text-[#394150]"}`}>
    <span className="shrink-0">{icon}</span>
    <span className="leading-snug">{text}</span>
  </button>
));

const SearchBar = ({
  onSearch,
  onStartedTyping,
  buttonText = "Ask Mila",
  placeholder = "Type your question here",
  question,
  UploaderComponent,
  isUploading = false
}) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSug, setIsLoadingSug] = useState(false);
  const [activeSugIndex, setActiveSugIndex] = useState(-1);

  // Use refs directly — no need to mirror state into refs via useEffect
  const analyticsRef = useRef(null);
  const showSuggestionsRef = useRef(false);
  const suggestionsRef = useRef([]);
  const queryRef = useRef("");
  const suggestionUsedRef = useRef(false);

  // Keep refs in sync synchronously during renders (no useEffect overhead)
  showSuggestionsRef.current = showSuggestions;
  suggestionsRef.current = suggestions;
  queryRef.current = query;

  const milaContext = useMilaContext();
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const abortRef = useRef(null);

  // GetCurrentUserData() reads localStorage synchronously — call directly each render (cheap).
  // useMemo with [] would freeze the value at mount-time, missing tokens set after first render.
  const userData = CurrentUser.GetCurrentUserData();
  const nounUserId = userData?.userId ?? null;
  const nounOuId = milaContext?.rootOu?.rootOu ?? userData?.ouId ?? null;
  const rootOU = milaContext?.rootOu?.rootOu ?? userData?.ouId ?? null;

  const [mentionQuery, setMentionQuery] = useState(null);
  const { suggestions: mentionSuggestions, isLoading: isMentionLoading } =
    useMentionCompletion(mentionQuery, nounOuId);

  const { detections, updateAfterReplace, updateAfterMention } =
    useNounRecognition(query, nounUserId, nounOuId, !!mentionQuery);

  const handleNounReplace = useCallback((newText, replacedDetection, candidateText) => {
    updateAfterReplace(replacedDetection, candidateText, newText);
    queryRef.current = newText;
    setQuery(newText);
  }, [updateAfterReplace]);

  const handleMentionApply = useCallback((newText) => {
    updateAfterMention(newText);
    setMentionQuery(null);
    queryRef.current = newText;
    setQuery(newText);
  }, [updateAfterMention]);

  const handleAnalytics = useCallback((ga) => {
    if (ga) analyticsRef.current = ga;
  }, []);

  useEffect(() => {
    if (question) {
      setQuery(question);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [question]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        if (showSuggestionsRef.current && suggestionsRef.current.length > 0) {
          analyticsRef.current?.trackEventAnalytics(
            "Suggestion",
            "SuggestionIgnored",
            queryRef.current.trim()
          );
        }
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (value) => {
    if (value.trim().length < MIN_CHARS) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Cancel any in-flight request before starting a new one
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoadingSug(true);
    try {
      const userData = await CurrentUser.GetCurrentUserData();

      // Bail if a newer request has already replaced this one
      if (controller.signal.aborted) return;

      const result = await apiClient.autoSuggestions(
        value,
        userData?.userId,
        milaContext?.rootOu?.rootOu ?? userData?.ouId,
        controller.signal
      );

      if (controller.signal.aborted) return;

      if (Array.isArray(result) && result.length > 0) {
        setSuggestions(result);
        setShowSuggestions(true);

        analyticsRef.current?.trackEventAnalytics(
          "Suggestion",
          "SuggestionImpression",
          result
            .slice(0, 5)
            .map((s) => s.text)
            .join(" | ")
        );
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      if (!controller.signal.aborted) setIsLoadingSug(false);
    }
  }, []);

  const handleSubmit = useCallback((e) => {
    e?.preventDefault();
    setShowSuggestions(false);

    const currentQuery = queryRef.current;
    if (currentQuery.trim() || milaContext?.uploadedFiles?.length > 0) {
      analyticsRef.current?.trackEventAnalytics(
        "Suggestion",
        suggestionUsedRef.current
          ? "SubmissionAfterSuggestion"
          : "SubmissionWithoutSuggestion",
        currentQuery.trim()
      );
      suggestionUsedRef.current = false;
      onSearch(currentQuery);
    }
  }, [milaContext?.uploadedFiles, onSearch]);

  const handleSelectSuggestion = useCallback((text) => {
    const clicked = suggestionsRef.current.find((s) => s.text === text);
    analyticsRef.current?.trackEventAnalytics(
      "Suggestion",
      "SuggestionClicked",
      `[${clicked?.source ?? "unknown"}] ${text}`
    );

    suggestionUsedRef.current = true;
    // Update ref immediately so handleSubmit reads the right value
    queryRef.current = text;
    setQuery(text);
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveSugIndex(-1);

    // Call submit directly — no useState flag + useEffect round-trip needed
    setTimeout(() => handleSubmit(), 0);
  }, [handleSubmit]);

  const handleChange = useCallback((e) => {
    const value = e.target.value;
    setQuery(value);
    setActiveSugIndex(-1);
    suggestionUsedRef.current = false;

    clearTimeout(debounceRef.current);

    if (value.trim().length < MIN_CHARS) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceRef.current = setTimeout(() => fetchSuggestions(value), DEBOUNCE_MS);
  }, [fetchSuggestions]);

  const handleKeyDown = useCallback((e) => {
    if (!showSuggestionsRef.current || suggestionsRef.current.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSugIndex((prev) => (prev + 1) % suggestionsRef.current.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSugIndex((prev) => (prev <= 0 ? suggestionsRef.current.length - 1 : prev - 1));
    } else if (e.key === "Enter" && activeSugIndex >= 0) {
      e.preventDefault();
      handleSelectSuggestion(suggestionsRef.current[activeSugIndex].text);
    } else if (e.key === "Escape") {
      if (showSuggestionsRef.current && suggestionsRef.current.length > 0) {
        analyticsRef.current?.trackEventAnalytics(
          "Suggestion",
          "SuggestionIgnored",
          queryRef.current.trim()
        );
      }
      setShowSuggestions(false);
      setActiveSugIndex(-1);
    }
  }, [activeSugIndex, handleSelectSuggestion]);

  // Derived values — computed once per render, not on every map() call
  const { personalSuggestions, globalSuggestions } = useMemo(() => ({
    personalSuggestions: suggestions.filter((s) => s.source === "personal"),
    globalSuggestions: suggestions.filter((s) => s.source === "global"),
  }), [suggestions]);

  const isDropdownOpen = showSuggestions && suggestions.length > 0;

  return (
    <div className="w-full" ref={containerRef}>
      <GrtAnalyticsGA4 onLoaded={handleAnalytics} />

      <div className="relative">
        <div className={`flex flex-col peer border border-[#F0E5FB] bg-white
          ${isDropdownOpen
            ? "rounded-t-[20px] rounded-b-none border-b-0"
            : "rounded-[20px]"}
          pt-0 overflow-hidden transition-all duration-150`}>
          <form onSubmit={handleSubmit}>
            <NounAwareTextArea
              rowsCount="1"
              value={query}
              onChangeEvent={handleChange}
              placeholderLabel={placeholder}
              classNames="noun-textarea w-full px-7 py-5 text-lg border-0 focus:outline-none text-black placeholder:text-[#A6ABB6] resize-none rounded-t-lg"
              onStartedTyping={onStartedTyping}
              onKeyDown={handleKeyDown}
              detections={detections}
              onReplace={handleNounReplace}
              rootOU={rootOU}
              mentionSuggestions={mentionSuggestions}
              isMentionLoading={isMentionLoading}
              onMentionQueryChange={setMentionQuery}
              onMentionApply={handleMentionApply}
            />

            <div className="flex justify-between items-center px-3 py-0">
              {UploaderComponent}
              <div className="flex items-center space-x-5">
                <div
                  className="py-3 pr-0 rounded-base rounded-l-none border-l-0 peer-focus:outline-none peer-focus:border-blue-200"
                  style={{ fontSize: "14px" }}>
                  <Button
                    type="submit"
                    className="max-w-[140px] max-h-[42px]"
                    disabled={isUploading}>
                    <img src={icon} alt="AskMila" />
                    {buttonText}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>

        {isDropdownOpen && (
          <div className="absolute z-50 w-full bg-white border border-[#F0E5FB] border-t-0
                          rounded-b-[20px] shadow-lg overflow-hidden">

            {isLoadingSug && (
              <div className="px-5 py-3 flex items-center gap-2 text-sm text-[#A6ABB6]">
                <span className="inline-block w-3 h-3 rounded-full bg-[#D8B4FE] animate-pulse" />
                Searching suggestions…
              </div>
            )}

            {!isLoadingSug && (
              <>
                {personalSuggestions.length > 0 && (
                  <div>
                    <div className="px-5 pt-0 pb-1 text-[11.5px] font-semibold uppercase
                                    tracking-widest text-[#B4B4B4]">
                      SUGGESTED QUESTIONS
                    </div>
                    {personalSuggestions.map((s) => {
                      const idx = suggestions.indexOf(s);
                      return (
                        <SuggestionItem
                          key={`personal-${idx}`}
                          text={s.text}
                          isActive={activeSugIndex === idx}
                          onSelect={handleSelectSuggestion}
                          onHover={() => setActiveSugIndex(idx)}
                          icon={<span className="text-[#9333EA]"><PersonalIcon /></span>}
                        />
                      );
                    })}
                  </div>
                )}

                {personalSuggestions.length > 0 && globalSuggestions.length > 0 && (
                  <div className="mx-5 border-t border-[#F0E5FB]" />
                )}

                {globalSuggestions.length > 0 && (
                  <div className="pb-5">
                    <div className="px-5 pt-3 pb-1 text-[11.5px] font-semibold uppercase
                                    tracking-widest text-[#B4B4B4]">
                      Popular questions
                    </div>
                    {globalSuggestions.map((s) => {
                      const idx = suggestions.indexOf(s);
                      return (
                        <SuggestionItem
                          key={`global-${idx}`}
                          text={s.text}
                          isActive={activeSugIndex === idx}
                          onSelect={handleSelectSuggestion}
                          onHover={() => setActiveSugIndex(idx)}
                          icon={<span className="text-[#A6ABB6]"><PopularIcon /></span>}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBar;